<template>
  <div class="performanceBlockingChart">
    <div class="performanceBlockingChart__wrapper">
      <table class="performanceBlockingChart__table">
        <thead>
          <tr>
            <th
              v-for="(header, index) in levelHeaders"
              :key="header.value"
              class="performanceBlockingChart__header performanceBlockingChart__header--level"
              :style="getStickyColumnStyle(index, true)"
            >
              {{ header.text }}
            </th>
            <th
              v-for="(header, index) in summaryHeaders"
              :key="header.value"
              class="performanceBlockingChart__header performanceBlockingChart__header--summary"
              :style="getStickyColumnStyle(levelHeaders.length + index, true)"
            >
              {{ header.text }}
            </th>
            <th
              v-for="header in metricHeaders"
              :key="header.value"
              class="performanceBlockingChart__header performanceBlockingChart__header--metric"
            >
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in displayRows"
            :key="getRowKey(row, rowIndex)"
            class="performanceBlockingChart__row"
            :class="{
              'performanceBlockingChart__row--summary': !isLeafRow(row),
              'performanceBlockingChart__row--leaf': isLeafRow(row),
              'performanceBlockingChart__row--corporate': isCorporateRow(row)
            }"
            @click="handleRowClick(row)"
          >
            <td
              v-for="(header, index) in levelHeaders"
              :key="header.value"
              class="performanceBlockingChart__cell performanceBlockingChart__cell--level"
              :style="getLevelCellStyle(row, index)"
            >
              <v-tooltip
                top
                :disabled="getLevelDisplayValue(row, header.value) === ''"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    class="performanceBlockingChart__levelText"
                    :tabindex="getLevelDisplayValue(row, header.value) === '' ? -1 : 0"
                    v-on="on"
                  >
                    {{ getLevelDisplayValue(row, header.value) }}
                  </span>
                </template>
                <span class="performanceBlockingChart__tooltipText">
                  {{ getLevelDisplayValue(row, header.value) }}
                </span>
              </v-tooltip>
            </td>
            <td
              v-for="(header, index) in summaryHeaders"
              :key="header.value"
              class="performanceBlockingChart__cell performanceBlockingChart__cell--summary"
              :style="getSummaryCellStyle(row, index)"
            >
              {{ getSummaryCellValue(row, header.value) }}
            </td>
            <td
              v-for="header in metricHeaders"
              :key="header.value"
              class="performanceBlockingChart__cell performanceBlockingChart__cell--metric"
              :style="getMetricCellStyle(row, header.value)"
            >
              <span v-if="hasDisplayMetricValue(row, header.value)">
                {{ getMetricCellValue(row, header.value) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "PerformanceBlockingChart",
  props: {
    tableHeader: {
      type: Array,
      default: () => [],
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    selectedLevels: {
      type: Array,
      default: () => [],
    },
    tableRowClickFunction: {
      type: Function,
      default: null,
    },
    numericColumns: {
      type: Array,
      default: () => [],
    },
    currencyColumns: {
      type: Array,
      default: () => [],
    },
    dataBreakdownType: {
      type: String,
      default: "spend",
    },
    showMetricPercentages: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    levelColumnWidth: 150,
    summaryColumnWidth: 120,
    colorList: [
      "#A9C7FF",
      "#D7B8FF",
      "#FFB3C2",
      "#FFD37A",
      "#FFB199",
      "#91D8CF",
      "#A7D8FF",
      "#C8C2FF",
      "#F4B4D4",
      "#A8DDB5",
      "#F6C77B",
      "#A8E3F5",
      "#FFB0BD",
      "#C9E88B",
      "#D9B6F6",
      "#93DCE8",
      "#FFC29A",
      "#BFC6FF",
      "#9BE2B1",
      "#F3ADD2",
    ],
    summaryColorList: [
      "#FFFFFF",
      "#F0F0F0",
      "#DCDCDC",
      "#D0D0D0",
      "#BEBEBE",
      "#A9A9A9",
    ],
  }),
  computed: {
    safeHeaders() {
      return Array.isArray(this.tableHeader) ? this.tableHeader : [];
    },
    safeRows() {
      return Array.isArray(this.tableData) ? this.tableData : [];
    },
    safeSelectedLevels() {
      return Array.isArray(this.selectedLevels) ? this.selectedLevels : [];
    },
    safeNumericColumns() {
      return Array.isArray(this.numericColumns) ? this.numericColumns : [];
    },
    safeCurrencyColumns() {
      return Array.isArray(this.currencyColumns) ? this.currencyColumns : [];
    },
    levelHeaders() {
      return this.safeSelectedLevels.map((level) => {
        return this.safeHeaders.find((header) => header.value === level) || {
          text: level,
          value: level,
        };
      });
    },
    metricHeaders() {
      return this.safeHeaders.filter((header) => {
        return !this.safeSelectedLevels.includes(header.value) &&
          !this.isInternalColumn(header.value);
      });
    },
    summaryHeaders() {
      const headers = [
        {
          text: `Total ${this.metricLabel}`,
          value: "rowTotal",
        },
        {
          text: `% ${this.metricLabel}`,
          value: "rowPercentage",
        },
      ];

      headers.push(
        {
          text: "CPM",
          value: "rowCpm",
        },
        {
          text: "CPC",
          value: "rowCpc",
        }
      );

      return headers;
    },
    displayRows() {
      if (this.filteredRows.length === 0) {
        return [];
      }

      return [this.corporateRow].concat(this.sortedRows);
    },
    filteredRows() {
      return this.safeRows.filter((row) => this.rowHasMetricData(row));
    },
    sortedRows() {
      return this.filteredRows.slice().sort((rowA, rowB) => {
        for (let index = 0; index < this.safeSelectedLevels.length; index += 1) {
          const level = this.safeSelectedLevels[index];
          const result = this.compareLevelValues(rowA[level], rowB[level]);

          if (result !== 0) {
            return result;
          }
        }

        return 0;
      });
    },
    parentColorLevels() {
      if (this.safeSelectedLevels.length <= 1) {
        return this.safeSelectedLevels;
      }

      return this.safeSelectedLevels.slice(0, -1);
    },
    parentColorKeys() {
      return this.sortedRows
        .filter((row) => this.isLeafRow(row))
        .map((row) => this.getParentColorKey(row))
        .filter((key, index, colorKeys) => {
          return key !== "" && colorKeys.indexOf(key) === index;
        });
    },
    colorMap() {
      return this.parentColorKeys.reduce((map, colorKey, index) => {
        map[colorKey] = this.colorList[index % this.colorList.length];
        return map;
      }, {});
    },
    metricType() {
      if (this.dataBreakdownType === "impressions") {
        return "impressions";
      }

      if (this.dataBreakdownType === "clicks") {
        return "clicks";
      }

      return "spend";
    },
    metricLabel() {
      if (this.metricType === "impressions") {
        return "Impr.";
      }

      if (this.metricType === "clicks") {
        return "Clicks";
      }

      return "Spend";
    },
    totalSourceRows() {
      const leafRows = this.filteredRows.filter((row) => this.isLeafRow(row));
      return leafRows.length > 0 ? leafRows : this.filteredRows;
    },
    corporatePeriodTotals() {
      return this.metricHeaders.reduce((totals, header) => {
        totals[header.value] = this.totalSourceRows.reduce((total, row) => {
          return total + this.getNumericValue(row[header.value]);
        }, 0);

        return totals;
      }, {});
    },
    corporateMetricTotals() {
      return this.totalSourceRows.reduce((totals, row) => {
        const rowTotals = this.getRowMetricTotals(row);

        totals["spend"] += rowTotals["spend"];
        totals["impressions"] += rowTotals["impressions"];
        totals["clicks"] += rowTotals["clicks"];

        return totals;
      }, {
        spend: 0,
        impressions: 0,
        clicks: 0,
      });
    },
    corporateTotal() {
      return this.metricHeaders.reduce((total, header) => {
        return total + this.getNumericValue(this.corporatePeriodTotals[header.value]);
      }, 0);
    },
    corporateRow() {
      const row = {
        __isCorporateRow: true,
        __metricTotals: this.corporateMetricTotals,
      };

      this.safeSelectedLevels.forEach((level, index) => {
        row[level] = index === 0 ? "Overall" : null;
      });

      this.metricHeaders.forEach((header) => {
        row[header.value] = this.corporatePeriodTotals[header.value];
      });

      return row;
    },
  },
  methods: {
    compareLevelValues(valueA, valueB) {
      const aIsEmpty = valueA == null || valueA === "";
      const bIsEmpty = valueB == null || valueB === "";

      if (aIsEmpty && bIsEmpty) {
        return 0;
      }

      if (aIsEmpty) {
        return -1;
      }

      if (bIsEmpty) {
        return 1;
      }

      return String(valueA).localeCompare(String(valueB), undefined, { numeric: true });
    },
    getRowKey(row, rowIndex) {
      const levelKey = this.safeSelectedLevels
        .map((level) => row[level] == null ? "" : row[level])
        .join("__");

      return `${rowIndex}-${levelKey}`;
    },
    isLeafRow(row) {
      if (this.isCorporateRow(row)) {
        return false;
      }

      if (this.safeSelectedLevels.length === 0) {
        return true;
      }

      return this.safeSelectedLevels.every((level) => {
        return row[level] != null && row[level] !== "";
      });
    },
    getSummaryDepth(row) {
      let missingCount = 0;

      for (let index = 0; index < this.safeSelectedLevels.length; index += 1) {
        const level = this.safeSelectedLevels[index];

        if (row[level] == null || row[level] === "") {
          missingCount += 1;
        }
      }

      return missingCount;
    },
    getSummaryBackground(row) {
      if (this.isCorporateRow(row)) {
        return "#8F8F8F";
      }

      const colorIndex = Math.min(
        this.getSummaryDepth(row),
        this.summaryColorList.length - 1
      );

      return this.summaryColorList[colorIndex];
    },
    getLevelCellStyle(row, index) {
      return {
        ...this.getStickyColumnStyle(index, false),
        backgroundColor: this.isLeafRow(row) ? "#FFFFFF" : this.getSummaryBackground(row),
      };
    },
    getStickyColumnStyle(index, isHeader) {
      const leftOffset = this.getStickyColumnLeft(index);
      const columnWidth = this.getStickyColumnWidth(index);

      return {
        left: `${leftOffset}px`,
        minWidth: `${columnWidth}px`,
        width: `${columnWidth}px`,
        maxWidth: `${columnWidth}px`,
        zIndex: isHeader ? 5 : 3,
      };
    },
    getStickyColumnLeft(index) {
      const levelColumnCount = this.levelHeaders.length;

      if (index <= levelColumnCount) {
        return index * this.levelColumnWidth;
      }

      return (levelColumnCount * this.levelColumnWidth) +
        ((index - levelColumnCount) * this.summaryColumnWidth);
    },
    getStickyColumnWidth(index) {
      return index < this.levelHeaders.length
        ? this.levelColumnWidth
        : this.summaryColumnWidth;
    },
    getSummaryCellStyle(row, index) {
      return {
        ...this.getStickyColumnStyle(this.levelHeaders.length + index, false),
        backgroundColor: this.isLeafRow(row) ? "#FFFFFF" : this.getSummaryBackground(row),
      };
    },
    getMetricCellStyle(row, column) {
      if (!this.isLeafRow(row)) {
        return {
          backgroundColor: this.getSummaryBackground(row),
        };
      }

      if (!this.hasDisplayMetricValue(row, column)) {
        return {
          backgroundColor: "#FFFFFF",
        };
      }

      return {
        backgroundColor: this.getGroupColor(row),
        color: "#111111",
      };
    },
    getParentColorKey(row) {
      if (!row || this.parentColorLevels.length === 0) {
        return "";
      }

      return this.parentColorLevels
        .map((level) => row[level])
        .filter((value) => value != null && value !== "")
        .map((value) => String(value))
        .join("||");
    },
    isCorporateRow(row) {
      return row && row.__isCorporateRow === true;
    },
    isInternalColumn(column) {
      return typeof column === "string" && column.indexOf("__") === 0;
    },
    getGroupColor(row) {
      const colorKey = this.getParentColorKey(row);

      if (colorKey === "") {
        return this.colorList[0];
      }

      return this.colorMap[colorKey] || this.colorList[0];
    },
    getLevelDisplayValue(row, col) {
      if (this.isCorporateRow(row)) {
        return row[col] || "";
      }

      const value = this.levelValue(row, col);
      return value == null ? "" : String(value);
    },
    levelValue(row, col) {
      const levelIndex = this.safeSelectedLevels.indexOf(col);

      if (levelIndex < 0) {
        return row[col];
      }

      for (
        let index = this.safeSelectedLevels.length - 1;
        index > levelIndex;
        index -= 1
      ) {
        const level = this.safeSelectedLevels[index];

        if (row[level] != null && row[level] !== "") {
          return "";
        }
      }

      return row[col];
    },
    hasDisplayMetricValue(row, col) {
      const value = this.getNumericValue(row[col]);

      return Number.isFinite(value) && value !== 0;
    },
    rowHasMetricData(row) {
      return this.metricHeaders.some((header) => {
        return this.hasDisplayMetricValue(row, header.value);
      });
    },
    getRowTotal(row) {
      return this.metricHeaders.reduce((total, header) => {
        return total + this.getNumericValue(row[header.value]);
      }, 0);
    },
    getMetricCellValue(row, col) {
      const formattedValue = this.formatMetricValue(row[col], col);
      if (!this.showMetricPercentages) {
        return formattedValue;
      }

      const percentage = this.corporateTotal === 0
        ? 0
        : this.getNumericValue(row[col]) / this.corporateTotal;
      const formattedPercentage = this.formatPercentage(percentage);

      return formattedPercentage
        ? `${formattedValue} (${formattedPercentage})`
        : formattedValue;
    },
    getSummaryCellValue(row, col) {
      if (col === "rowTotal") {
        return this.formatMetricValue(this.getRowTotal(row), col);
      }

      if (col === "rowPercentage") {
        const rowTotal = this.getRowTotal(row);
        const percentage = this.corporateTotal === 0 ? 0 : rowTotal / this.corporateTotal;
        return this.formatPercentage(percentage);
      }

      if (col === "rowCpm") {
        return this.formatCostMetric(this.getRowCpm(row));
      }

      if (col === "rowCpc") {
        return this.formatCostMetric(this.getRowCpc(row));
      }

      return "";
    },
    getRowMetricTotals(row) {
      const metricTotals = row && row["__metricTotals"];

      return {
        spend: this.getNumericValue(metricTotals && metricTotals["spend"]),
        impressions: this.getNumericValue(metricTotals && metricTotals["impressions"]),
        clicks: this.getNumericValue(metricTotals && metricTotals["clicks"]),
      };
    },
    getRowCpm(row) {
      const metricTotals = this.getRowMetricTotals(row);
      if (metricTotals["impressions"] === 0) {
        return null;
      }

      return (metricTotals["spend"] / metricTotals["impressions"]) * 1000;
    },
    getRowCpc(row) {
      const metricTotals = this.getRowMetricTotals(row);
      if (metricTotals["clicks"] === 0) {
        return null;
      }

      return metricTotals["spend"] / metricTotals["clicks"];
    },
    getNumericValue(value) {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    },
    formatCostMetric(value) {
      if (!Number.isFinite(value) || value === 0) {
        return "";
      }

      return value.toLocaleString("en-CA", {
        style: "currency",
        currency: "CAD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatMetricValue(value, col) {
      const numericValue = this.getNumericValue(value);

      if (!Number.isFinite(numericValue) || numericValue === 0) {
        return "";
      }

      if (this.metricType === "spend" || this.safeCurrencyColumns.includes(col)) {
        return numericValue.toLocaleString("en-CA", {
          style: "currency",
          currency: "CAD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }

      if (this.metricType === "impressions" || this.metricType === "clicks" || this.safeNumericColumns.includes(col)) {
        return numericValue.toLocaleString("en-CA", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      }

      return numericValue.toLocaleString("en-CA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatPercentage(value) {
      if (!Number.isFinite(value) || value === 0) {
        return "";
      }

      return value.toLocaleString("en-CA", {
        style: "percent",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    handleRowClick(row) {
      if (this.tableRowClickFunction && this.isLeafRow(row) && !this.isCorporateRow(row)) {
        this.tableRowClickFunction(row);
      }
    },
  },
};
</script>

<style scoped>
.performanceBlockingChart {
  margin: 12px;
}

.performanceBlockingChart__wrapper {
  max-height: calc(100vh - 400px);
  overflow: auto;
  width: 100%;
}

.performanceBlockingChart__table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 100%;
  table-layout: fixed;
}

.performanceBlockingChart__header {
  background: #FFFFFF;
  border-bottom: 1px solid #E5E7EB;
  color: #6B7280;
  font-family: "Poppins" !important;
  font-size: 12px;
  font-weight: 600;
  height: 42px;
  padding: 0 12px;
  position: sticky;
  text-align: center;
  top: 0;
  white-space: nowrap;
}

.performanceBlockingChart__header--level {
  text-align: left;
}

.performanceBlockingChart__header--summary {
  text-align: center;
}

.performanceBlockingChart__header--metric {
  min-width: 150px;
  width: 150px;
}

.performanceBlockingChart__cell {
  border-bottom: 1px solid #FFFFFF;
  box-sizing: border-box;
  font-family: "Poppins" !important;
  font-size: 14px;
  font-weight: 600;
  height: 46px;
  overflow: hidden;
  padding: 0 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.performanceBlockingChart__cell--level {
  position: sticky;
  text-align: left;
}

.performanceBlockingChart__cell--summary {
  position: sticky;
  text-align: center;
}

.performanceBlockingChart__cell--metric {
  min-width: 150px;
  text-align: center;
  width: 150px;
}

.performanceBlockingChart__levelText {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.performanceBlockingChart__tooltipText {
  display: block;
  max-width: 320px;
  white-space: normal;
  word-break: break-word;
}

.performanceBlockingChart__row {
  cursor: pointer;
}

.performanceBlockingChart__row--summary {
  cursor: default;
}

.performanceBlockingChart__row--corporate {
  cursor: default;
}

.performanceBlockingChart__row--summary .performanceBlockingChart__cell {
  color: #222222;
}

.performanceBlockingChart__row--corporate .performanceBlockingChart__cell {
  color: #111111;
}

.performanceBlockingChart__row--leaf .performanceBlockingChart__cell--metric {
  border-left: 1px solid #FFFFFF;
}
</style>
